// src/components/Modals/AdminHelpDeskModal.jsx

import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box, Container, Typography, Button, TextField, IconButton,
  Chip, Alert, InputAdornment, Select, MenuItem, FormControl,
  Dialog, Stack, Avatar, LinearProgress, AppBar, Toolbar, Slide,
  Card, CardContent, CircularProgress
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

import { helpDeskService, ROLES } from '../../services/helpDeskService';

// ─── Pure UI Helpers ──────────────────────────────────────────────────────────
const getColorForPriority = (priority) => {
  const map = {
    Critical: { bg: '#ffebee', text: '#c62828', muiColor: 'error' },
    High:     { bg: '#fff3e0', text: '#ef6c00', muiColor: 'warning' },
    Medium:   { bg: '#e3f2fd', text: '#1565c0', muiColor: 'primary' },
    Low:      { bg: '#e8f5e9', text: '#2e7d32', muiColor: 'success' },
  };
  return map[priority] || { bg: '#ececec', text: '#595959', muiColor: 'default' };
};

const getColorForStatus = (status) => {
  const map = {
    Open:          { bg: '#e3f2fd', text: '#1565c0', muiColor: 'primary' },
    'In Progress': { bg: '#fff9c4', text: '#9a8900', muiColor: 'warning' },
    Resolved:      { bg: '#e8f5e9', text: '#2e7d32', muiColor: 'success' },
    Closed:        { bg: '#ececec', text: '#666',    muiColor: 'default' },
  };
  return map[status] || { bg: '#ececec', text: '#595959', muiColor: 'default' };
};

const getColorForIssueType = (issueType) => {
  if (issueType === 'Audit') {
    return { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' };
  }
  return { bg: '#fdf6e4', text: '#bc7800', border: '#edd286' };
};

const avatarLetters = (emailOrName = '') => {
  if (!emailOrName || typeof emailOrName !== 'string') return 'NA';
  try {
    return emailOrName.split(' ').map(x => x[0] || '').join('').slice(0, 2).toUpperCase() || 'NA';
  } catch { return 'NA'; }
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(amount);

// ─── Slide Transition ─────────────────────────────────────────────────────────
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ═════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════
const FormRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '1rem',
  marginBottom: '1rem',
  '&:last-child': { marginBottom: 0 },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== 'hasError' && p !== 'layoutType',
})(({ hasError, layoutType }) => ({
  gridColumn: layoutType === 'full' ? '1 / -1' : layoutType === 'compact' ? 'span 1' : 'span 2',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset': { borderColor: hasError ? '#dc2626' : '#d1d5db' },
    '&:hover fieldset': { borderColor: hasError ? '#dc2626' : '#3b82f6' },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#dc2626' : '#3b82f6',
      boxShadow: hasError ? '0 0 0 3px rgba(220,38,38,0.12)' : '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.025em',
}));

const FieldHint = styled(Typography)(() => ({
  fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic',
}));

const SectionCard = styled(Card)(() => ({
  borderRadius: 8, border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.25rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb',
}));

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontWeight: 500, textTransform: 'none',
  '&:hover': { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1, backgroundColor: '#2563eb', color: '#fff', borderRadius: '6px', fontWeight: 600, textTransform: 'none', minWidth: '160px', display: 'flex', alignItems: 'center', gap: 1,
  '&:hover': { backgroundColor: '#1d4ed8' },
  '&:disabled': { backgroundColor: '#93c5fd' },
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

export default function AdminHelpDeskModal({
  open,
  onClose,
  issue,
  setIssue,
  onUpdate,
  technicians,
  currentUser,
  showNotification,
}) {
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionCost, setResolutionCost] = useState('');
  const [maintenanceInvoice, setMaintenanceInvoice] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isResolving, setIsResolving] = useState(false);
  const [allowedAssignees, setAllowedAssignees] = useState([]);
 const actorEmail = String(currentUser?.email || '').trim().toLowerCase();
  const actorRole  = String(currentUser?.role  || '').trim().toLowerCase();
  const isAdmin    = actorRole === 'admin' || actorRole === 'superadmin';
  const assignedEmail = String(issue?.assignedToEmail || '').trim().toLowerCase();
  const isAssignee = !!assignedEmail && assignedEmail === actorEmail;

  const canEditResolution = isAssignee || (isAdmin && !assignedEmail);

  // Sync state when dialog opens
   useEffect(() => {
    if (!open || !issue) return;

    setIssue((prev) => ({
      ...prev,
      _originalAssignedToUid:   issue.assignedToUid   || null,
      _originalAssignedToEmail: issue.assignedToEmail || null,
      _originalStatus:          issue.status || "Open",
    }));

    setResolutionNotes(issue.resolutionNotes || '');
    setResolutionCost(issue.resolutionCost ? issue.resolutionCost.toString() : '');
    setMaintenanceInvoice(null);
    setUploading(false);
    setUploadProgress(0);
  
  }, [open]);

  // Load the Ticket Receivers list whenever the dialog opens
  useEffect(() => {
    if (!open) {
      setAllowedAssignees([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { companyId } = await helpDeskService.resolveAdminContext();
        const list = await helpDeskService.getAssignableUsersByReceiverRoles(companyId);
        if (!cancelled) setAllowedAssignees(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("[AdminHelpDeskModal] Failed to load receivers:", err);
        if (!cancelled) setAllowedAssignees([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    const previousPaddingRight = html.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    html.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      html.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      html.style.overflow = previousOverflow;
      html.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try { helpDeskService.validateFile(file); setMaintenanceInvoice(file); }
    catch (err) { showNotification(err.message, 'error'); }
  };

  const handleViewInvoice = (url, fileName) => {
    try { helpDeskService.openInvoiceFile(url, fileName); }
    catch (err) { showNotification(err.message, 'error'); }
  };

  const handleDialogClose = () => {
    if (isResolving || uploading) return;
    onClose();
  };

   const handleStatusChange = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!issue) return;

    setIsResolving(true);
    try {
      // ── 1. Detect assignment change against the snapshot taken on open ──
      const originalUid   = String(issue?._originalAssignedToUid   || "");
      const originalEmail = String(issue?._originalAssignedToEmail || "").trim().toLowerCase();
      const nextUid       = String(issue?.assignedToUid   || "");
      const nextEmail     = String(issue?.assignedToEmail || "").trim().toLowerCase();

      const assignmentChanged =
        originalUid !== nextUid || originalEmail !== nextEmail;

      // ── 2. Persist assignment FIRST (server allows only Open / In Progress) ──
      if (assignmentChanged) {
        await helpDeskService.updateIssue(
          issue.id,
          {
            assignedToUid:   nextUid   || null,
            assignedToEmail: nextEmail || null,
          },
          currentUser,
        );
      }

      // ── 3. Build the status / resolution payload (NO assignment fields) ──
      const newStatus = issue.status;
      const updateData = {
        status: newStatus,
        resolvedBy: currentUser?.email || "Unknown",
      };

      if (newStatus === "Resolved" || newStatus === "Closed") {
        updateData.resolvedAt = issue._resolvedAtRaw || new Date().toISOString();
        updateData.resolutionNotes = resolutionNotes;
        if (resolutionCost && !isNaN(parseFloat(resolutionCost))) {
          updateData.resolutionCost = parseFloat(resolutionCost);
        }
        if (maintenanceInvoice) {
          const fileData = await helpDeskService.convertFileToBase64(
            maintenanceInvoice,
            currentUser?.email,
            (pct) => { setUploading(true); setUploadProgress(pct); },
          );
          updateData.maintenanceInvoice = fileData;
        }
      }

        // Only call a SECOND update when non-assignment content actually changed.
      const contentChanged =
        newStatus !== (issue._originalStatus || "Open") ||
        updateData.resolutionNotes !== undefined ||
        updateData.resolutionCost !== undefined ||
        updateData.maintenanceInvoice !== undefined;

      if (contentChanged) {
        await helpDeskService.updateIssue(issue.id, updateData, currentUser);
      }
      try {
        await helpDeskService.sendStatusNotification(
          issue.userId, issue.id, currentUser?.email || "Admin", newStatus,
        );
      } catch (notifyErr) {
        console.warn("[AdminHelpDeskModal] notification failed:", notifyErr);
      }

           onUpdate(
        issue.id,
        {
          ...updateData,
          assignedToUid:   nextUid   || null,
          assignedToEmail: nextEmail || null,
        },
        newStatus,
      );
           showNotification(
        assignmentChanged && !contentChanged
          ? "Ticket re-assigned successfully"
          : `Ticket successfully ${String(newStatus).toLowerCase()}`,
        "success",
      );
      handleDialogClose();
    } catch (err) {
      showNotification(`Failed to update: ${err.message}`, "error");
    } finally {
      setIsResolving(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

     const isSubmitDisabled =
    isResolving ||
    uploading;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleDialogClose}
      TransitionComponent={Transition}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleDialogClose} disabled={isResolving} aria-label="Close">
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Ticket #{String(issue?.id || '').substring(0, 8)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {String(issue?.userEmail || '')}
              {issue?.raisedBy === ROLES.AUDITOR ? ' · Auditor ticket' : ' · User ticket'}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box component="form" id="helpdesk-form" onSubmit={handleStatusChange} noValidate sx={{ flex: 1, overflowY: 'auto', py: 4 }}>
          <Container maxWidth="lg">
            <Stack spacing={3}>

              {/* ===== SECTION 1: TICKET OVERVIEW ===== */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
                    <Avatar sx={{
                      bgcolor: issue?.raisedBy === ROLES.AUDITOR ? '#f3e5f5' : '#d1e1fa',
                      color:   issue?.raisedBy === ROLES.AUDITOR ? '#6a1b9a' : '#1976d2',
                      fontWeight: 600, width: 36, height: 36, fontSize: 16,
                    }}>
                      {avatarLetters(issue?.userEmail || issue?.userName)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' }}>
                        Ticket Overview
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Submitted by {String(issue?.userEmail || 'N/A')}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2.5}>
                    <Chip label={String(issue?.status || 'Open')} sx={{ bgcolor: getColorForStatus(issue?.status).bg, color: getColorForStatus(issue?.status).text, fontWeight: 700, px: 1.5 }} />
                    <Chip label={`Priority: ${String(issue?.priority || 'Medium')}`} sx={{ bgcolor: getColorForPriority(issue?.priority).bg, color: getColorForPriority(issue?.priority).text, fontWeight: 700, px: 1.5 }} />
                    <Chip size="small" label={issue?.raisedBy === ROLES.AUDITOR ? 'Auditor ticket' : 'User ticket'} sx={{ fontWeight: 600, bgcolor: issue?.raisedBy === ROLES.AUDITOR ? '#f3e5f5' : '#e8f4fd', color: issue?.raisedBy === ROLES.AUDITOR ? '#6a1b9a' : '#0d5c8f' }} />
                    {issue?.resolvedAt && <Chip size="small" label="Resolved" color="success" />}
                  </Stack>

                  <FormRow>
                    <FormGroup layoutType="compact">
                      <FieldLabel>Issue Type</FieldLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip variant="outlined" size="small" label={String(issue?.issueType || 'General')} sx={{ ...getColorForIssueType(issue?.issueType), fontWeight: 600 }} />
                        {issue?.raisedBy === ROLES.AUDITOR && issue?._originalIssueType && (
                          <Typography variant="caption" color="text.secondary">Sub-type: <strong>{issue._originalIssueType}</strong></Typography>
                        )}
                      </Box>
                    </FormGroup>
                    <FormGroup layoutType="compact">
                      <FieldLabel>Created</FieldLabel>
                      <Typography variant="body2" mt={0.5}>{String(issue?.createdAt || 'N/A')}</Typography>
                    </FormGroup>
                    {issue?.resolvedAt && (
                      <FormGroup layoutType="compact">
                        <FieldLabel>Resolved At</FieldLabel>
                        <Typography variant="body2" color="success.dark" fontWeight={500} mt={0.5}>{String(issue.resolvedAt)}</Typography>
                      </FormGroup>
                    )}
                  </FormRow>
                </CardContent>
              </SectionCard>

              {/* ===== SECTION 2: DESCRIPTION ===== */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Description</SectionTitle>
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 15, color: 'text.primary' }}>
                    {String(issue?.description || 'No description provided.')}
                  </Typography>
                </CardContent>
              </SectionCard>

              {/* ===== SECTION 3: AUDIT CONTEXT ===== */}
              {issue?.raisedBy === ROLES.AUDITOR && (
                <SectionCard variant="outlined" sx={{ borderColor: '#ce93d8' }}>
                  <CardContent sx={{ p: 3, bgcolor: '#faf4ff', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#6a1b9a', marginBottom: '1.25rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e0c7ea' }}>
                      Audit Context
                    </Typography>
                    <FormRow>
                      {issue?.auditEventTitle && (
                        <FormGroup layoutType="compact">
                          <FieldLabel>Audit Event</FieldLabel>
                          <Typography variant="body2" mt={0.5} fontWeight={600}>{issue.auditEventTitle}</Typography>
                        </FormGroup>
                      )}
                      {issue?.lastKnownLocation && (
                        <FormGroup layoutType="compact">
                          <FieldLabel>Last Known Location</FieldLabel>
                          <Typography variant="body2" mt={0.5} fontWeight={600}>{issue.lastKnownLocation}</Typography>
                        </FormGroup>
                      )}
                      {issue?.expectedLocation && (
                        <FormGroup layoutType="compact">
                          <FieldLabel>Expected Location</FieldLabel>
                          <Typography variant="body2" mt={0.5} fontWeight={600}>{issue.expectedLocation}</Typography>
                        </FormGroup>
                      )}
                    </FormRow>
                  </CardContent>
                </SectionCard>
              )}

              {/* ===== SECTION 4: EXISTING RESOLUTION INFO ===== */}
              {(issue?.resolutionNotes || issue?.resolutionCost || issue?.maintenanceInvoice) && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>Existing Resolution Details</SectionTitle>
                    <FormRow>
                      {issue?.resolutionNotes && (
                        <FormGroup layoutType="full">
                          <FieldLabel>Resolution Notes</FieldLabel>
                          <Box sx={{ bgcolor: '#f4f6fa', p: 1.5, borderRadius: 1.5, mt: 0.5 }}>
                            <Typography variant="body2">{String(issue.resolutionNotes)}</Typography>
                          </Box>
                        </FormGroup>
                      )}
                    </FormRow>
                    <FormRow>
                      {issue?.resolutionCost && (
                        <FormGroup layoutType="compact">
                          <FieldLabel>Resolution Cost</FieldLabel>
                          <Box sx={{ bgcolor: '#f4f6fa', p: 1.5, borderRadius: 1.5, mt: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>{formatCurrency(issue.resolutionCost)}</Typography>
                          </Box>
                        </FormGroup>
                      )}
                      {issue?.maintenanceInvoice && (
                        <FormGroup layoutType="full">
                          <FieldLabel>Maintenance Invoice</FieldLabel>
                          <Box sx={{ bgcolor: '#f4f6fa', p: 1.5, borderRadius: 1.5, mt: 0.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <AttachFileIcon fontSize="small" />
                              <Typography sx={{ flexGrow: 1 }} variant="body2">{issue.maintenanceInvoice.fileName}</Typography>
                              <IconButton size="small" onClick={() => handleViewInvoice(issue.maintenanceInvoice.downloadURL, issue.maintenanceInvoice.fileName)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        </FormGroup>
                      )}
                    </FormRow>
                  </CardContent>
                </SectionCard>
              )}

              {/* ===== SECTION 5: ASSIGNMENT ===== */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Assignment</SectionTitle>
                                 {allowedAssignees.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5, py: 0.5 }}>
                      Only users with roles defined in the <strong>Ticket Receivers</strong> catalog can be assigned to tickets.
                    </Alert>
                  )}
                  {(issue?.status === 'Resolved' || issue?.status === 'Closed') && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
                      Assignment is locked because this ticket is <strong>{issue?.status}</strong>. Re-assignment is only allowed while the ticket is <strong>Open</strong> or <strong>In Progress</strong>.
                    </Alert>
                  )}
                  <FormRow>
                    <FormGroup layoutType="full">
                      <FieldLabel>Assigned Technician</FieldLabel>
                      <FormControl fullWidth size="small">
                        <Select
                                                   value={issue?.assignedToEmail || issue?.assignedToUid || ''}
                          disabled={issue?.status === 'Resolved' || issue?.status === 'Closed'}
                          onChange={(e) => {
                            const emailVal = e.target.value;
                            const picked = emailVal ? allowedAssignees.find(a => a.email === emailVal) : null;
                            
                            // Only update the local form state. The actual save happens on Submit.
                            setIssue(prev => ({ 
                              ...prev, 
                              assignedToUid: picked ? String(picked.uid) : null, 
                              assignedToEmail: picked ? String(picked.email) : null 
                            }));
                          }}
                          displayEmpty
                        >
                          <MenuItem value=""><em>Unassigned</em></MenuItem>
                          {allowedAssignees.map((receiver) => (
                            <MenuItem key={receiver.uid || receiver.email} value={receiver.email}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {receiver.name || receiver.email}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {receiver.email} · <span style={{ textTransform: 'capitalize' }}>{receiver.role}</span>
                                  </Typography>
                                </Box>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FormGroup>
                  </FormRow>
                </CardContent>
              </SectionCard>

                            {/* ===== SECTION 6: UPDATE STATUS ===== */}
              {canEditResolution && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>Update Status</SectionTitle>

                    {/* Status Dropdown */}
                    <FormRow>
                      <FormGroup layoutType="compact">
                        <FieldLabel>New Status</FieldLabel>
                        <FormControl fullWidth size="small">
                          <Select
                            value={issue?.status || 'Open'}
                            onChange={(e) => setIssue(prev => ({ ...prev, status: e.target.value }))}
                          >
                            {/* Fallback for current status if not in list */}
                            {issue?.status && !['Open', 'In Progress', 'Resolved', 'Closed'].includes(issue.status) && (
                              <MenuItem value={issue.status}>{issue.status}</MenuItem>
                            )}
                            <MenuItem value="Open">Open</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Resolved">Resolved</MenuItem>
                            <MenuItem value="Closed">Closed</MenuItem>
                          </Select>
                        </FormControl>
                      </FormGroup>
                    </FormRow>

                    {/* Resolution Fields - Only show if Resolved or Closed */}
                    {(issue?.status === 'Resolved' || issue?.status === 'Closed') && (
                      <>
                        <FormRow>
                          <FormGroup layoutType="full">
                            <FieldLabel>
                              Resolution Notes
                              <Typography component="span" sx={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.7rem', textTransform: 'none', marginLeft: '0.25rem' }}>
                                (visible to user)
                              </Typography>
                            </FieldLabel>
                            <TextField multiline rows={4} value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} fullWidth size="small" placeholder="Enter resolution details..." />
                          </FormGroup>
                        </FormRow>
                        <FormRow>
                          <FormGroup layoutType="compact">
                            <FieldLabel>Resolution Cost</FieldLabel>
                            <TextField type="number" value={resolutionCost} onChange={e => setResolutionCost(e.target.value)} fullWidth size="small" placeholder="Enter cost amount" InputProps={{ startAdornment: (<InputAdornment position="start">₹</InputAdornment>) }} inputProps={{ min: 0, step: 0.01 }} />
                          </FormGroup>
                        </FormRow>
                        <FormRow>
                          <FormGroup layoutType="full">
                            <FieldLabel>Upload Maintenance Invoice</FieldLabel>
                            <Box>
                              <input accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls" style={{ display: 'none' }} id="maintenance-invoice-upload" type="file" onChange={handleFileSelect} />
                              <label htmlFor="maintenance-invoice-upload">
                                <Button variant="outlined" component="span" startIcon={<AttachFileIcon />} disabled={uploading}>
                                  {maintenanceInvoice ? 'Change File' : 'Select Invoice File'}
                                </Button>
                              </label>

                              {maintenanceInvoice && (
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5 }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <AttachFileIcon fontSize="small" />
                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                      {maintenanceInvoice.name} ({(maintenanceInvoice.size / 1024).toFixed(2)} KB)
                                    </Typography>
                                    <IconButton size="small" onClick={() => setMaintenanceInvoice(null)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </Box>
                              )}
                              {uploading && (
                                <Box sx={{ mt: 1.5 }}>
                                  <LinearProgress variant="determinate" value={uploadProgress} />
                                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>Processing... {uploadProgress}%</Typography>
                                </Box>
                              )}
                              <FieldHint sx={{ mt: 1, display: 'block' }}>Supported formats: PDF, JPG, PNG, Excel (Max 700KB)</FieldHint>
                            </Box>
                          </FormGroup>
                        </FormRow>
                      </>
                    )}
                  </CardContent>
                </SectionCard>
              )}
            </Stack>
          </Container>
        </Box>

        <Box sx={{ borderTop: '1px solid #e2e8f0', backgroundColor: '#fff', py: 2, px: 3, boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', flexShrink: 0 }}>  <Container maxWidth={false} disableGutters>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button type="button" onClick={handleDialogClose} disabled={isResolving} sx={cancelBtnSx}>Cancel</Button>
              <Button type="submit" form="helpdesk-form" disabled={isSubmitDisabled} sx={submitBtnSx}>
                {isResolving && <CircularProgress size={16} color="inherit" />}
                {isResolving ? 'Processing…' : 'Submit'}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </Dialog>
  );
}